/**
 * Financial Period Filters Component
 * Enhanced filters and search for financial periods
 */

import React, { useState, useEffect } from 'react';
import './FinancialPeriodFilters.css';

const FinancialPeriodFilters = ({
  filters,
  onFiltersChange,
  isProcessing
}) => {
  const [localFilters, setLocalFilters] = useState(filters);

  // Update local state when props change
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);


  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    onFiltersChange(localFilters);
  };

  // Handle reset
  const handleReset = () => {
    const resetFilters = {
      is_active: '',
      start_date_after: '',
      start_date_before: '',
      end_date_after: '',
      end_date_before: '',
      ordering: '-start_date'
    };
    setLocalFilters(resetFilters);
    onFiltersChange(resetFilters);
  };

  // Handle clear filters
  const handleClearFilters = () => {
    const clearedFilters = {
      ...localFilters,
      is_active: '',
      start_date_after: '',
      start_date_before: '',
      end_date_after: '',
      end_date_before: ''
    };
    setLocalFilters(clearedFilters);
    onFiltersChange(clearedFilters);
  };

  // Handle real-time filter changes
  const handleFilterChange = (name, value) => {
    const newFilters = {
      ...localFilters,
      [name]: value
    };
    setLocalFilters(newFilters);
    onFiltersChange(newFilters);
  };

  // Get active filters count
  const getActiveFiltersCount = () => {
    let count = 0;
    if (localFilters.is_active) count++;
    if (localFilters.start_date_after) count++;
    if (localFilters.start_date_before) count++;
    if (localFilters.end_date_after) count++;
    if (localFilters.end_date_before) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="financial-period-filters">
      <form onSubmit={handleSearch} className="filters-form">
        <div className="filters-content">
          {/* Main Filters Grid */}
          <div className="filters-grid">
            <div className="filter-group">
              <label htmlFor="is_active">Active Status</label>
              <select
                id="is_active"
                name="is_active"
                value={localFilters.is_active || ''}
                onChange={(e) => handleFilterChange('is_active', e.target.value)}
                disabled={isProcessing}
              >
                <option value="">All Statuses</option>
                <option value="true">✅ Active Only</option>
                <option value="false">❌ Inactive Only</option>
              </select>
            </div>

            <div className="filter-group">
              <label htmlFor="start_date_after">Start After</label>
              <input
                type="date"
                id="start_date_after"
                name="start_date_after"
                value={localFilters.start_date_after || ''}
                onChange={(e) => handleFilterChange('start_date_after', e.target.value)}
                disabled={isProcessing}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="start_date_before">Start Before</label>
              <input
                type="date"
                id="start_date_before"
                name="start_date_before"
                value={localFilters.start_date_before || ''}
                onChange={(e) => handleFilterChange('start_date_before', e.target.value)}
                disabled={isProcessing}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="end_date_after">End After</label>
              <input
                type="date"
                id="end_date_after"
                name="end_date_after"
                value={localFilters.end_date_after || ''}
                onChange={(e) => handleFilterChange('end_date_after', e.target.value)}
                disabled={isProcessing}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="end_date_before">End Before</label>
              <input
                type="date"
                id="end_date_before"
                name="end_date_before"
                value={localFilters.end_date_before || ''}
                onChange={(e) => handleFilterChange('end_date_before', e.target.value)}
                disabled={isProcessing}
              />
            </div>

            <div className="filter-group">
              <label htmlFor="ordering">Sort By</label>
              <select
                id="ordering"
                name="ordering"
                value={localFilters.ordering || '-start_date'}
                onChange={(e) => handleFilterChange('ordering', e.target.value)}
                disabled={isProcessing}
              >
                <option value="-start_date">📅 Start Date (Newest)</option>
                <option value="start_date">📅 Start Date (Oldest)</option>
                <option value="-end_date">📅 End Date (Newest)</option>
                <option value="end_date">📅 End Date (Oldest)</option>
                <option value="name">📝 Name (A-Z)</option>
                <option value="-name">📝 Name (Z-A)</option>
                <option value="-created_at">🕒 Created (Newest)</option>
                <option value="created_at">🕒 Created (Oldest)</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="filters-actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleClearFilters}
              disabled={isProcessing || activeFiltersCount === 0}
            >
              Clear Filters
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleReset}
              disabled={isProcessing}
            >
              Reset All
            </button>
            {activeFiltersCount > 0 && (
              <div className="active-filters-count">
                {activeFiltersCount} filter{activeFiltersCount !== 1 ? 's' : ''} active
              </div>
            )}
          </div>
        </div>

        {/* Active Filters Summary */}
        {activeFiltersCount > 0 && (
          <div className="active-filters-summary">
            <div className="summary-header">
              <span className="summary-title">Active Filters:</span>
            </div>
            <div className="active-filters-list">
              {localFilters.is_active && (
                <span className="active-filter-tag">
                  Status: {localFilters.is_active === 'true' ? 'Active' : 'Inactive'}
                  <button
                    type="button"
                    className="remove-filter"
                    onClick={() => {
                      const newFilters = { ...localFilters, is_active: '' };
                      setLocalFilters(newFilters);
                      onFiltersChange(newFilters);
                    }}
                  >
                    ×
                  </button>
                </span>
              )}
              {localFilters.start_date_after && (
                <span className="active-filter-tag">
                  Start After: {localFilters.start_date_after}
                  <button
                    type="button"
                    className="remove-filter"
                    onClick={() => {
                      const newFilters = { ...localFilters, start_date_after: '' };
                      setLocalFilters(newFilters);
                      onFiltersChange(newFilters);
                    }}
                  >
                    ×
                  </button>
                </span>
              )}
              {localFilters.start_date_before && (
                <span className="active-filter-tag">
                  Start Before: {localFilters.start_date_before}
                  <button
                    type="button"
                    className="remove-filter"
                    onClick={() => {
                      const newFilters = { ...localFilters, start_date_before: '' };
                      setLocalFilters(newFilters);
                      onFiltersChange(newFilters);
                    }}
                  >
                    ×
                  </button>
                </span>
              )}
              {localFilters.end_date_after && (
                <span className="active-filter-tag">
                  End After: {localFilters.end_date_after}
                  <button
                    type="button"
                    className="remove-filter"
                    onClick={() => {
                      const newFilters = { ...localFilters, end_date_after: '' };
                      setLocalFilters(newFilters);
                      onFiltersChange(newFilters);
                    }}
                  >
                    ×
                  </button>
                </span>
              )}
              {localFilters.end_date_before && (
                <span className="active-filter-tag">
                  End Before: {localFilters.end_date_before}
                  <button
                    type="button"
                    className="remove-filter"
                    onClick={() => {
                      const newFilters = { ...localFilters, end_date_before: '' };
                      setLocalFilters(newFilters);
                      onFiltersChange(newFilters);
                    }}
                  >
                    ×
                  </button>
                </span>
              )}
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default FinancialPeriodFilters;
